
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for viewing Dashboard users for Monitor',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Dashboard User Audit API' subtitle='API reference for viewing Dashboard users for Monitor' compact mdxType="Header" />
    <p>{`These endpoints are used to look up a Dashboard user, as referenced in an `}<inlineCode parentName="p">{`audit_trail`}</inlineCode>{` object from the `}<a parentName="p" {...{
        "href": "/docs/api/products/monitor"
      }}>{`Monitor`}</a>{` API. `}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/dashboard_user/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retrieve information about Dashboard user`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/dashboard_user/list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List Dashboard users`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "dashboard_userget"
    }}>{`/dashboard_user/get`}</h4>
    <Schema method='POST' route='/dashboard_user/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/dashboard_user/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: DashboardUserGetRequest = {
  dashboard_user_id: '54350110fedcbaf01234ffee',
};

try {
  const response = await client.dashboardUserGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/dashboard_user/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "dashboard_user_id": "54350110fedcbaf01234ffee"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::DashboardUserGetRequest.new(
  {
    dashboard_user_id: '54350110fedcbaf01234ffee',
  }
)

response = client.dashboard_user_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`DashboardUserGetRequest request = new DashboardUserGetRequest()
  .dashboardUserId("54350110fedcbaf01234ffee");

Response<DashboardUserGetResponse> response = client()
  .dashboardUserGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = DashboardUserGetRequest(
  dashboard_user_id='54350110fedcbaf01234ffee',
)
response = client.dashboard_user_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewDashboardUserGetRequest("54350110fedcbaf01234ffee")

response, _, err := client.PlaidApi.
  DashboardUserGet(ctx).
  DashboardUserGetRequest(*request).
  Execute()
if err != nil {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.DashboardUserGetRequest{DashboardUserID: "54350110fedcbaf01234ffee"}

response, err := client.DashboardUser.Get(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/dashboard_user/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "dashboard_userlist"
    }}>{`/dashboard_user/list`}</h4>
    <Schema method='POST' route='/dashboard_user/list' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/dashboard_user/list' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`try {
  const response = await client.dashboardUserList({});
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/dashboard_user/list \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`response = client.dashboard_user_list({})
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`Response<DashboardUserListResponse> response = client()
  .dashboardUserList(new DashboardUserListRequest())
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = DashboardUserListRequest(
  cursor='eyJkaXJlY3Rpb24iOiJuZXh0Iiwib2Zmc2V0IjoiMTU5NDM',
)
response = client.dashboard_user_list(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewDashboardUserListRequest()

response, _, err := client.PlaidApi.
  DashboardUserList(ctx).
  DashboardUserListRequest(*request).
  Execute()
if err != nil {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.DashboardUserListRequest{}

response, err := client.DashboardUser.List(ctx, request)
if err != nil {
    // handle error
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/dashboard_user/list' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "dashboard_userget",
        level: 4,
        title: "/dashboard_user/get",
      },
{
        id: "dashboard_userlist",
        level: 4,
        title: "/dashboard_user/list",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
